import { useMutation } from '@apollo/client'
import {
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import RemoveIcon from '@material-ui/icons/Close'
import DeleteIcon from '@material-ui/icons/Delete'
import DoneIcon from '@material-ui/icons/Done'
import LinkIcon from '@material-ui/icons/Link'
import RetryIcon from '@material-ui/icons/Refresh'
import { memo, useEffect, useState } from 'react'
import { FileWithPath } from 'react-dropzone'
import { toast } from 'react-toastify'

import {
  mutationsFor,
  SupportedUploadPair
} from '../../graphql/uploadPerEntity'
import { UploadFileData } from '../../types/upload'
import { ConfirmActionModal } from '../ConfirmActionModal'
import ImageBoxSkeleton from './ImageBox.skeleton'
import useStyles from './styles/index.styles'

export enum FileStatus {
  UPLOADING,
  ERROR,
  COMPLETED,
  DELETING,
  TYPING_NAME
}

export type ImageProps = {
  file?: FileWithPath
  fileURL: string
  fileName: string
  status: FileStatus
  fileSize: number
  mediaID: string
}

// entityType/imageType come from SupportedUploadPair, which correlates them — see its
// comment. They are not two independent props: mutationsFor resolves a mutation from the
// PAIR, and an unsupported pair throws during render with no error boundary above it, so it
// has to be a build error rather than a blank page.
type ImageBoxProps = SupportedUploadPair & {
  item: ImageProps
  onChange: (newIamge: ImageProps) => void
  onDelete: (fileURL: string) => void
  entityid: string
  entityCode?: string
  roomTypeCode?: string
}

const ImageBox: React.FC<ImageBoxProps> = ({
  item,
  onChange,
  onDelete,
  entityType,
  entityid,
  entityCode,
  imageType,
  roomTypeCode
}) => {
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))

  const [pageIsReady, setPageIsReady] = useState<boolean>(
    item.status === FileStatus.TYPING_NAME
  )
  const [imageLoaded, setimageLoaded] = useState(false)
  const classes = useStyles()
  const [percent] = useState(0)
  const [error, seterror] = useState(false)
  const [fileName, setfileName] = useState<string>(item.fileName)
  const [cancelReservModalOpen, setCancelReservModalOpen] = useState(false)

  // Resolved from the entity/image pair rather than one generic mutation: IAM authorizes on
  // the root field name, so each entity needs its own field to carry its own permission.
  // See graphql/uploadPerEntity.ts.
  const perEntity = mutationsFor(entityType, imageType)

  const [upload] = useMutation(perEntity.upload, {
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      onChange({
        ...item,
        status: FileStatus.COMPLETED
      })
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })
  const [deleteImage, { loading: loadingForDeleteImage }] = useMutation(
    perEntity.remove,
    {
      onCompleted: (data) => {
        // Every per-entity mutation aliases its payload to `result`, so this reads the
        // same key whichever entity is in play.
        toast.success(data.result.message)
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )
  const handleCompletedName = async () => {
    setPageIsReady(true)
  }
  const handleUploadImage = async () => {
    setPageIsReady(true)
    onChange({
      ...item,
      status: FileStatus.UPLOADING
    })
    seterror(false)
    const uploadFileData: UploadFileData = {
      entityID: entityid,
      entityCode: entityCode || '',
      entityType: entityType,
      file: item.file!,
      imageType: imageType
    }

    upload({
      variables: uploadFileData
    })
  }

  useEffect(() => {
    if (item.status === FileStatus.UPLOADING) {
      handleUploadImage()
    } else if (item.status === FileStatus.COMPLETED) {
      handleCompletedName()
    }
  }, [])

  const handleDeleteFromServer = () => {
    onChange({
      ...item,
      status: FileStatus.DELETING
    })
    deleteImage({
      variables: {
        mediaID: item.mediaID,
        entityCode: entityCode,
        // Declared only by deleteRoomTypeImage; the others ignore the extra key.
        roomTypeCode: roomTypeCode
      }
    })

    onDelete(item.fileURL)
  }

  const ShowOptions = () => {
    switch (item.status) {
      case FileStatus.COMPLETED:
        return (
          <>
            <IconButton
              className={classes.linkIcon}
              size={isSmall ? 'small' : 'medium'}>
              <a href={item.fileURL} target="'_blank'">
                <LinkIcon />
              </a>
            </IconButton>
            <IconButton
              data-cy="deleteIconForImage"
              onClick={() => setCancelReservModalOpen(true)}
              size={isSmall ? 'small' : 'medium'}>
              <RemoveIcon />
            </IconButton>
          </>
        )
      case FileStatus.UPLOADING:
        return <CircularProgress size={30} />
      case FileStatus.ERROR:
        return (
          <>
            <IconButton onClick={handleUploadImage}>
              <RetryIcon />
            </IconButton>
            <IconButton onClick={() => onDelete(item.fileURL)}>
              <RemoveIcon />
            </IconButton>
          </>
        )
      case FileStatus.DELETING:
        return <CircularProgress size={30} />
      case FileStatus.TYPING_NAME:
        return (
          <>
            <IconButton onClick={handleUploadImage}>
              <DoneIcon />
            </IconButton>
            <IconButton onClick={() => onDelete(item.fileURL)}>
              <RemoveIcon />
            </IconButton>
          </>
        )
      default:
        return null
    }
  }
  if (pageIsReady)
    return (
      <Paper
        className={[classes.item, error ? classes.itemError : ''].join(' ')}
        elevation={4}>
        {item.status === FileStatus.UPLOADING && (
          <div
            className={classes.progressbar}
            style={{
              width: `${percent}%`
            }}
          />
        )}
        <div className={classes.parentDiv}>
          <div className={classes.thumbnail}>
            <img
              src={item.fileURL}
              className={[
                classes.img,
                imageLoaded ? classes.imgVisible : ''
              ].join(' ')}
              alt={item.fileName}
              onLoad={() => setimageLoaded(true)}
            />
          </div>
          <div className={classes.details}>
            {item.status === FileStatus.TYPING_NAME ? (
              <TextField
                fullWidth
                defaultValue={fileName.substr(0, fileName.indexOf('.'))}
                onChange={(event) => setfileName(event.target.value)}
              />
            ) : (
              <Typography variant="body1">
                {fileName.length < 4 || !isSmall
                  ? fileName
                  : `${fileName.substr(0, 3)}...`}
              </Typography>
            )}
            <Typography variant="caption">
              {item.fileSize
                ? `${parseInt((item.fileSize / 1000).toString(), 10)} kb`
                : ''}
            </Typography>
          </div>
          {ShowOptions()}
        </div>
        {cancelReservModalOpen && (
          <ConfirmActionModal
            icon={<DeleteIcon className={classes.iconDelete} />}
            title={'Image is about to be deleted'}
            subtitle={'Are you sure you want to delete it permanently?'}
            signature={'Delete Me'}
            onClose={() => setCancelReservModalOpen(false)}
            isOpen={cancelReservModalOpen}
            onClick={() => {
              handleDeleteFromServer()
            }}
            loading={loadingForDeleteImage}
          />
        )}
      </Paper>
    )
  return <ImageBoxSkeleton />
}

export default memo(ImageBox)
